import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertNotification } from '@givebutter/primitives/alert-notification';
import { useToast } from '@givebutter/primitives/toast';
import { addPaymentAccounts, fetchPaymentAccounts } from 'api/paymentAccounts';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import * as Form from 'components/form';
import { CHARIOT_HELP_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { PaymentAccount } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { PaginatedData } from 'types/pagination';

interface ChariotFormValues {
    email: string;
}

const Chariot = () => {
    const { success, error } = useToast();
    const { accountId, email } = useCurrentAccount();

    const { data: paymentIntegrations } = useQuery<
        PaginatedData<PaymentAccount>,
        GivebutterAxiosError
    >({
        queryKey: accountQueryKeys.paymentAccounts(accountId),
        queryFn: () => fetchPaymentAccounts(accountId),
    });
    const chariotIntegration = paymentIntegrations?.data?.find(
        (item: PaymentAccount) => item.merchant === 'chariot',
    );
    const isIntegrationEnabled = !!chariotIntegration?.id;

    const methods = Form.useGivebutterForm<ChariotFormValues>({
        defaultValues: {
            email: email ?? '',
        },
    });
    const { handleSubmit, path, control } = methods;

    const { mutate: submit, isPending: isSubmitting } = useMutation<
        unknown,
        GivebutterAxiosError,
        ChariotFormValues
    >({
        mutationFn: ({ email }) => addPaymentAccounts(accountId, { email, merchant: 'chariot' }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.paymentAccounts(accountId),
            });
            success('Account Added.');
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="Chariot"
                        className="flex max-w-[100px]"
                        src={`/images/logos/chariot.png`}
                    />
                </IntegrationItem.Logo>
                <IntegrationItem.Description>
                    <p className="text-xl font-medium">Chariot</p>

                    <p className="max-w-xs text-base text-gray-700">
                        Accept Donor-Advised Fund donations directly through your Givebutter
                        donation forms.{' '}
                        <a target="_blank" rel="noopener noreferrer" href={CHARIOT_HELP_LINK}>
                            Learn More
                        </a>
                    </p>
                    <p>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://givechariot.com/"
                        >
                            https://givechariot.com
                        </a>
                    </p>
                </IntegrationItem.Description>
                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                {isIntegrationEnabled ? (
                    <AlertNotification type="success">
                        Chariot integration enabled
                    </AlertNotification>
                ) : (
                    <Form.Provider {...methods}>
                        <div className="flex flex-col items-start gap-4">
                            <p className="text-sm">
                                Givebutter partners with{' '}
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://givechariot.com/"
                                >
                                    Chariot
                                </a>{' '}
                                to accept DAF donations. To get started, enter your email below and
                                click "Start trial". Once enabled, you'll immediately be able to
                                start accepting DAF donations.{' '}
                            </p>
                            <AlertNotification type="info">
                                You'll receive an email from Chariot to finish setting up your
                                account and to add payment information.
                            </AlertNotification>
                            <form
                                onSubmit={handleSubmit(values => submit(values))}
                                className="flex flex-row items-center gap-4"
                            >
                                <Form.Container className="flex flex-col gap-y-5">
                                    <div className="flex flex-row items-end gap-4">
                                        <div className="flex w-full grow flex-col">
                                            <Form.Label name={path('email')}>
                                                Email address
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                name="email"
                                                control={control}
                                                rules={{
                                                    required: 'Valid email is required.',
                                                }}
                                            >
                                                <Form.TextInputForm
                                                    type="email"
                                                    placeholder="Enter your email"
                                                />
                                            </Form.TextInputRoot>
                                        </div>
                                        <Form.SubmitButton
                                            disabled={isSubmitting}
                                            isSubmitting={isSubmitting}
                                        >
                                            Start Trial
                                        </Form.SubmitButton>
                                    </div>
                                </Form.Container>
                            </form>
                        </div>
                    </Form.Provider>
                )}
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { Chariot };
